import type {
  AccountType,
  EntityType,
  BusinessStructure,
  DeliveryFrequency,
  StandardTruckType,
  ProductSource,
  SourceIndustry,
  ContactType,
} from "../schemas/validators/enums";
import type { PaymentTerms } from "../schemas/validators/quoteEnums";
import { literalValues } from "@shared/validation/literal-values";

export type {
  AccountType,
  EntityType,
  BusinessStructure,
  DeliveryFrequency,
  StandardTruckType,
  ProductSource,
  SourceIndustry,
  ContactType,
  RfqPricingType,
  DocumentType,
  PaymentPreference,
  SupportPriority,
  BillingInterval,
  OwnerType,
  SearchResultType,
} from "../schemas/validators/enums";
export type { PaymentTerms } from "../schemas/validators/quoteEnums";
export type { ContactRole } from "../schemas/validators/contactCompanyEnums";
export { CONTACT_ROLE, CONTACT_DOCUMENT, CONTACT_DOCUMENTS } from "../schemas/validators/contactCompanyEnums";
export type { ContactDocument } from "../schemas/validators/contactCompanyEnums";

export { RFQ_FORM_TYPES as RFQ_TYPES, type RfqType } from "./leads";

export const ACCOUNT_TYPE = {
  BUYER: "buyer",
  VENDOR: "vendor",
  FREIGHT_CARRIER: "freight_carrier",
} as const satisfies Record<string, AccountType>;

export const ACCOUNT_TYPES = literalValues(ACCOUNT_TYPE);

export const COMPANY_TYPE_FILTERS = [
  ...ACCOUNT_TYPES,
  "prospect",
  "buyer_and_vendor",
] as const;

export const ENTITY_TYPE = {
  BUSINESS: "business",
  INDIVIDUAL: "individual",
} as const satisfies Record<string, EntityType>;

export const ENTITY_TYPES = literalValues(ENTITY_TYPE);

export const BUSINESS_STRUCTURE = {
  SOLE_PROPRIETORSHIP: "sole_proprietorship",
  SINGLE_MEMBER_LLC: "single_member_llc",
  MULTI_MEMBER_LLC: "multi_member_llc",
  PRIVATE_PARTNERSHIP: "private_partnership",
  PRIVATE_CORPORATION: "private_corporation",
  PUBLIC_CORPORATION: "public_corporation",
  INCORPORATED_NON_PROFIT: "incorporated_non_profit",
  UNINCORPORATED_NON_PROFIT: "unincorporated_non_profit",
  GOVERNMENT_INSTRUMENTALITY: "government_instrumentality",
  GOVERNMENTAL_UNIT: "governmental_unit",
  TAX_EXEMPT_GOVERNMENT_INSTRUMENTALITY:
    "tax_exempt_government_instrumentality",
  UNINCORPORATED_ASSOCIATION: "unincorporated_association",
  PUBLIC_PARTNERSHIP: "public_partnership",
} as const satisfies Record<string, BusinessStructure>;

export const BUSINESS_STRUCTURES = literalValues(BUSINESS_STRUCTURE);

export {
  ORDER_SOURCE,
  ORDER_SOURCES,
  ORDER_SOURCE_CHANNEL,
  ORDER_SOURCE_CHANNELS,
  ORDER_SOURCE_TO_CHANNEL,
  orderSourceChannelOf,
} from "./orderSource";
export type { OrderSource, OrderSourceChannel } from "./orderSource";

export const PAYMENT_TERMS = {
  PREPAY: "prepay",
  PAYMENT_ON_DELIVERY: "payment_on_delivery",
  DUE_ON_RECEIPT: "due_on_receipt",
  NET_7: "net_7",
  NET_15: "net_15",
  NET_30: "net_30",
  NET_45: "net_45",
  NET_60: "net_60",
  NET_90: "net_90",
  CUSTOM: "custom",
} as const satisfies Record<string, PaymentTerms>;

export const PAYMENT_TERMS_OPTIONS = literalValues(PAYMENT_TERMS);

export const DEFAULT_VENDOR_PAYMENT_TERMS = PAYMENT_TERMS.NET_30;

export const DELIVERY_FREQUENCY = {
  ONE_TIME: "one-time",
  WEEKLY: "weekly",
  BI_WEEKLY: "bi-weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  CUSTOM: "custom",
} as const satisfies Record<string, DeliveryFrequency>;

export const DELIVERY_FREQUENCY_VALUES = literalValues(DELIVERY_FREQUENCY);

export const STANDARD_TRUCK_TYPE = {
  DRY_VAN: "dryVan",
  FLATBED: "flatbed",
  BOX_TRUCK: "boxTruck",
  STEP_DECK: "stepDeck",
  PICKUP_TRUCK: "pickupTruck",
  DROP_TRAILER: "dropTrailer",
} as const satisfies Record<string, StandardTruckType>;

export const STANDARD_TRUCK_TYPES = literalValues(STANDARD_TRUCK_TYPE);

export const PRODUCT_SOURCE = {
  PUBLIC_FORM: "public-form",
  DASHBOARD: "dashboard",
  API: "api",
  IMPORT: "import",
} as const satisfies Record<string, ProductSource>;

export const PRODUCT_SOURCES = literalValues(PRODUCT_SOURCE);

export const SOURCE_INDUSTRY = {
  WAREHOUSING_3PL: "warehousing-3pl",
  MANUFACTURING: "manufacturing",
  RETAIL_ECOMMERCE: "retail-ecommerce",
  FOOD_BEVERAGE: "food-beverage",
  AUTOMOTIVE: "automotive",
  PHARMACEUTICALS: "pharmaceuticals",
  LOGISTICS: "logistics",
  OTHER: "other",
} as const satisfies Record<string, SourceIndustry>;

export const CONTACT_TYPE = {
  LEAD: "lead",
  PROSPECT: "prospect",
  BUYER: "buyer",
  VENDOR: "vendor",
  FREIGHT_CARRIER: "freight_carrier",
  BOTH: "both",
  CUSTOMER: "customer",
  SUPPLIER: "supplier",
} as const satisfies Record<string, ContactType>;

export const CONTACT_TYPES = literalValues(CONTACT_TYPE);

export const QUOTE_FULFILLMENT_TYPE = {
  PICKUP: "pickup",
  DELIVERY: "delivery",
} as const;

export type QuoteFulfillmentType =
  (typeof QUOTE_FULFILLMENT_TYPE)[keyof typeof QUOTE_FULFILLMENT_TYPE];

export type QuotePaymentTerms = PaymentTerms;

export const QUOTE_TAX_TYPE = {
  NONE: "none",
  MANUAL: "manual",
} as const;

export type QuoteTaxType = (typeof QUOTE_TAX_TYPE)[keyof typeof QUOTE_TAX_TYPE];
